/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventPublishedDialog
 * --------------------
 * Shown once a NEW event is published, from any step: the event's link to copy,
 * and the two ways on.
 *
 * Both ways leave the create page, and so does closing. Staying on
 * /organizer/events/create and saving again would write a second event, so the
 * dialog never simply closes back onto the form.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import EventShareLink from './EventShareLink';

// Into the organizer shell, as the check-in dialogs do: its theme variables
// are set there, so a dialog under <body> would lose its colours.
const shell = () => document.querySelector('.org-shell') || document.body;

/**
 * Whether a successful save opens this dialog: only a NEW event, and only when
 * it is published (a draft has no link to show) — from whichever step Publish
 * was pressed. An edit shows the link on step 3 already; every other save
 * leaves for the list.
 */
export const shouldShowPublishedDialog = ({ isEdit, status, eventId }) => (
  !isEdit && status === 'PUBLISHED' && eventId != null
);

export default function EventPublishedDialog({ event, onDone, onCertificates }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDone(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDone]);

  return createPortal(
    <div className="ochd-scrim" role="presentation" onClick={onDone}>
      <div
        className="ochd orgf-published"
        role="dialog" aria-modal="true" aria-labelledby="orgf-published-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ochd-head">
          <div>
            <h2 id="orgf-published-title">Event published</h2>
            <p className="orgf-help">{event.title ? `“${event.title}” is live.` : 'Your event is live.'} Share the link below.</p>
          </div>
          <button type="button" className="ochk-iconbtn" onClick={onDone} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>

        <EventShareLink eventId={event.id} status={event.status} visibility={event.visibility} />

        <div className="orgf-published-foot">
          <button type="button" className="org-btn ghost" onClick={onCertificates}>
            Set certificate templates
          </button>
          <button type="button" className="org-btn" onClick={onDone}>Done</button>
        </div>
      </div>
    </div>,
    shell(),
  );
}
